<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import BButton from '../BButton/BButton.vue'

import { useDefaults } from '../../composables'
import { BModalEvents, BModalProps, BModalSlots } from './use-modal'

const _props = withDefaults(defineProps<BModalProps>(), {
	zIndex: 200,
	scrim: true,
	teleportTo: 'body',
	focusLastActiveElement: true
})
const props = useDefaults(_props)

const emits = defineEmits<BModalEvents>()
defineSlots<BModalSlots>()

const model = ref(props.modelValue)
const modalId = `b-modal-${Math.random().toString(36).slice(2, 9)}`
const modalContainer = ref<Nullable<HTMLElement>>(null)
const lastActiveElement = ref<Nullable<Element>>(null)

watch(
	() => props.modelValue,
	(newValue) => {
		if (model.value !== newValue) {
			model.value = newValue
		}
	}
)
watch(
	() => model.value,
	(newValue) => {
		if (props.modelValue !== newValue) {
			emits('update:modelValue', newValue)
		}
		if (newValue) {
			document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`
			document.body.classList.add('overflow-hidden!')

			nextTick(() => {
				lastActiveElement.value = document.activeElement

				if (modalContainer.value) {
					modalContainer.value.focus()
				}
			})
		}
	}
)

const openModal = () => {
	model.value = true
}

const closeModal = () => {
	emits('dismiss')
	model.value = false
}

const onAfterLeave = () => {
	document.body.style.paddingRight = ''
	document.body.classList.remove('overflow-hidden!')
	emits('afterLeave')

	if (
		props.focusLastActiveElement &&
		lastActiveElement.value &&
		'focus' in lastActiveElement.value &&
		typeof lastActiveElement.value.focus === 'function'
	) {
		lastActiveElement.value.focus()
	}
}

const onOutsideClick = () => {
	if (!props.persistent) {
		closeModal()
	}
}

const onKeydown = (e: KeyboardEvent) => {
	if (e.key === 'Escape' && model.value && !props.persistent) {
		closeModal()
	}
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

const onSubmit = () => {
	emits('submit')
}

const onSecondaryClick = () => {
	emits('secondary-click')
}

const modalStyles = computed(() => {
	const styles: Record<string, string> = {}
	const formatDimension = (value: string | number | undefined, unit = 'px') =>
		value ? (isNaN(+value) ? `${value}` : `${value}${unit}`) : 'unset'
	if (props.fullscreen) {
		styles.width = '100dvw'
		styles.height = '100dvh'
	} else {
		styles.width = formatDimension(props.width || '100%')
		styles.height = formatDimension(props.height)
		styles.maxWidth = formatDimension(props.maxWidth || 1340)
		styles.maxHeight = formatDimension(props.maxHeight || '95%')
	}
	styles.zIndex = `${props.zIndex}`

	return styles
})

defineExpose({
	openModal,
	closeModal
})
</script>

<template>
	<slot
		:open="openModal"
		name="activator"
	/>

	<Teleport :to="props.teleportTo">
		<Transition @after-leave="onAfterLeave">
			<div
				v-if="model"
				:class="[
					'fixed inset-0 flex items-center justify-center ',
					{ 'bg-black/32': props.scrim }
				]"
				:style="{ zIndex: modalStyles.zIndex }"
				data-test="overlay"
				role="presentation"
				@click.self="onOutsideClick"
			>
				<div
					ref="modalContainer"
					:style="modalStyles"
					:aria-labelledby="props.title ? `${modalId}-title` : undefined"
					:aria-describedby="$slots.default ? `${modalId}-desc` : undefined"
					:id="modalId"
					class="@container bg-background-lowest rounded-lg flex flex-col gap-4 m-8 @sm:m-[50px] outline-0 overflow-hidden"
					role="dialog"
					aria-modal="true"
					tabindex="-1"
					data-test="modal"
				>
					<div
						v-if="!props.noHeader"
						class="flex items-center justify-between p-4 border-b border-outline-lighter"
					>
						<slot name="header">
							<span
								v-if="title"
								:id="`${modalId}-title`"
								class="text-label-3 text-on-surface-variant"
								role="heading"
								aria-level="2"
							>
								{{ props.title }}
							</span>

							<BButton
								v-if="props.closeIcon"
								:icon="
									typeof props.closeIcon === 'string'
										? props.closeIcon
										: 'cancel-01'
								"
								variant="link"
								color="secondary-text"
								aria-label="Close modal"
								@click="closeModal"
							/>
						</slot>
					</div>

					<div
						:class="props.bodyClass"
						:id="`${modalId}-desc`"
						class="grow p-4 text-body-3 overflow-auto"
					>
						<slot />
					</div>

					<div
						v-if="!props.noFooter"
						:class="{ 'justify-end': !props.secondaryText }"
						class="border-t border-outline-lighter flex flex-col gap-2 @sm:gap-4 justify-between items-center p-4 @sm:flex-row"
					>
						<slot name="footer">
							<BButton
								v-if="props.secondaryText"
								:text="props.secondaryText"
								:disabled="props.secondaryDisabled"
								:loading="props.secondaryLoading"
								variant="link"
								class="w-full @sm:w-fit"
								size="xs"
								@click="onSecondaryClick"
							/>

							<div class="flex items-center w-full @sm:w-fit gap-2 @sm:gap-4">
								<BButton
									v-if="props.submitText"
									:text="props.submitText"
									:disabled="props.submitDisabled"
									:loading="props.submitLoading"
									class="grow"
									size="xs"
									@click="onSubmit"
								/>

								<BButton
									v-if="props.dismissText"
									:text="props.dismissText"
									:disabled="props.dismissDisabled"
									:loading="props.dismissLoading"
									variant="outline"
									class="grow"
									size="xs"
									@click="closeModal"
								/>
							</div>
						</slot>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
	transition: all 0.3s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: scale(1.1);
}
</style>
